import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  commissionSite,
  listAdminSites,
  type AdminSite,
  type CommissionSiteRequest,
  type LifecycleResponse,
} from '../../api/adminClient';
import { recordPendingSteps } from '../../lib/pending-steps';
import {
  looksLikeJwt,
  looksLikeReference,
  validateRetentionDays,
  validateSiteId,
} from '../../lib/validation';

/**
 * Create or update a site and its deployment binding.
 *
 * One form for both, because the API is one idempotent endpoint —
 * `POST /sites/commission` updates an existing site's binding and
 * un-archives it. Splitting the UI into "create" and "edit" would
 * imply two different operations with different consequences, when
 * re-commissioning an archived site is specifically the supported way
 * to bring it back.
 *
 * The form's real output is not the database row — that part always
 * succeeds. It is `pending_operator_steps`: the cert, the ACL
 * regeneration, the bus restart and the worker deploy that this API
 * deliberately cannot perform. The site does not exist on the bus
 * until those run, so they are persisted rather than merely displayed
 * (see `lib/pending-steps.ts`).
 */

interface FormState {
  site_id: string;
  label: string;
  timezone: string;
  task_live_retention_days: string;
  task_history_retention_days: string;
  platform_bus_endpoint: string;
  zenoh_router_endpoint: string;
  rmf_session_uri: string;
  site_worker_command_keyexpr_prefix: string;
  broker_endpoint: string;
  broker_token_ref: string;
  deployment_manifest_version: string;
  worker_image_digest: string;
  certified_capabilities: string;
}

const EMPTY: FormState = {
  site_id: '',
  label: '',
  timezone: 'UTC',
  // Mirror the server defaults (`sites` column defaults 30/90): the
  // request always carries both values, so a commission of an
  // untouched form writes exactly what omitting them would have.
  task_live_retention_days: '30',
  task_history_retention_days: '90',
  // Defaults that match the running deployment, so the common case is
  // "type a site id and a label". Every one of these is the value a
  // new site on this platform bus actually needs.
  platform_bus_endpoint: 'tls/zenoh-router-platform:7447',
  zenoh_router_endpoint: '',
  rmf_session_uri: '/etc/rmw-zenoh/session-bridge-primary.json5',
  site_worker_command_keyexpr_prefix: '',
  broker_endpoint: '',
  broker_token_ref: '',
  deployment_manifest_version: '',
  worker_image_digest: '',
  certified_capabilities: '',
};

function fromSite(site: AdminSite): FormState {
  const b = site.binding;
  return {
    site_id: site.site_id,
    label: site.label,
    timezone: site.timezone,
    // `??` covers an api-server one release behind that does not echo
    // these yet; the fallbacks ARE the server defaults it would apply.
    task_live_retention_days: String(site.task_live_retention_days ?? 30),
    task_history_retention_days: String(site.task_history_retention_days ?? 90),
    platform_bus_endpoint: b?.platform_bus_endpoint ?? '',
    zenoh_router_endpoint: b?.zenoh_router_endpoint ?? '',
    rmf_session_uri: b?.rmf_session_uri ?? '',
    site_worker_command_keyexpr_prefix: b?.site_worker_command_keyexpr_prefix ?? '',
    broker_endpoint: b?.broker_endpoint ?? '',
    broker_token_ref: b?.broker_token_ref ?? '',
    deployment_manifest_version: b?.deployment_manifest_version ?? '',
    worker_image_digest: b?.worker_image_digest ?? '',
    certified_capabilities: (b?.certified_capabilities ?? []).join(', '),
  };
}

function toRequest(f: FormState): CommissionSiteRequest {
  const orNull = (v: string) => (v.trim() === '' ? null : v.trim());
  return {
    site_id: f.site_id.trim(),
    label: f.label.trim(),
    timezone: f.timezone.trim() || 'UTC',
    task_live_retention_days: Number(f.task_live_retention_days.trim()),
    task_history_retention_days: Number(f.task_history_retention_days.trim()),
    binding: {
      platform_bus_endpoint: orNull(f.platform_bus_endpoint),
      zenoh_router_endpoint: orNull(f.zenoh_router_endpoint),
      rmf_session_uri: orNull(f.rmf_session_uri),
      site_worker_command_keyexpr_prefix: orNull(f.site_worker_command_keyexpr_prefix),
      broker_endpoint: orNull(f.broker_endpoint),
      broker_token_ref: orNull(f.broker_token_ref),
      deployment_manifest_version: orNull(f.deployment_manifest_version),
      worker_image_digest: orNull(f.worker_image_digest),
      certified_capabilities: f.certified_capabilities
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0),
    },
  };
}

function TextField({
  label,
  value,
  onChange,
  hint,
  error,
  placeholder,
  required,
  type,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  hint?: string;
  error?: string | null;
  placeholder?: string;
  required?: boolean;
  type?: 'text' | 'number';
}) {
  return (
    <label style={{ display: 'block', marginBottom: 14 }}>
      <div style={{ fontSize: 13, marginBottom: 4 }}>
        {label}
        {required && <span style={{ color: 'var(--nx-error)' }}> *</span>}
      </div>
      {hint && (
        <div style={{ fontSize: 12, color: 'var(--nx-muted)', marginBottom: 4 }}>{hint}</div>
      )}
      <input
        value={value}
        type={type ?? 'text'}
        placeholder={placeholder}
        aria-label={label}
        aria-invalid={error ? true : undefined}
        onChange={(e) => onChange(e.target.value)}
        style={error ? { borderColor: 'var(--nx-error)' } : undefined}
      />
      {error && (
        <div role="alert" style={{ fontSize: 12, color: 'var(--nx-error)', marginTop: 4 }}>
          {error}
        </div>
      )}
    </label>
  );
}

export function CommissionPane() {
  const [params] = useSearchParams();
  const editingSiteId = params.get('site');
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [result, setResult] = useState<LifecycleResponse | null>(null);
  const [touched, setTouched] = useState(false);

  const sitesQuery = useQuery({
    queryKey: ['admin', 'sites'],
    queryFn: () => listAdminSites(true),
  });

  const existing = useMemo(
    () => (sitesQuery.data ?? []).find((s) => s.site_id === editingSiteId) ?? null,
    [sitesQuery.data, editingSiteId],
  );

  useEffect(() => {
    if (existing) setForm(fromSite(existing));
  }, [existing]);

  const set = (k: keyof FormState) => (v: string) => setForm((f) => ({ ...f, [k]: v }));

  const siteIdError = touched ? validateSiteId(form.site_id.trim()) : null;
  const labelError = touched && form.label.trim() === '' ? 'Required.' : null;
  const tokenIsJwt = looksLikeJwt(form.broker_token_ref);
  const tokenLooksWrong = !tokenIsJwt && !looksLikeReference(form.broker_token_ref.trim());

  // Ranges mirror the backend's pydantic constraints; the cross-field
  // rule needs both values so it lives here rather than validation.ts.
  const liveRetentionError = validateRetentionDays(form.task_live_retention_days, 7, 365);
  const historyRetentionError =
    validateRetentionDays(form.task_history_retention_days, 30, 730) ??
    (Number(form.task_history_retention_days.trim()) < Number(form.task_live_retention_days.trim())
      ? 'Must be at least the console retention — metrics/timeline history cannot be shorter than what the Tasks tab shows.'
      : null);

  const commission = useMutation({
    mutationFn: () => commissionSite(toRequest(form)),
    onSuccess: (resp) => {
      recordPendingSteps(resp);
      setResult(resp);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'sites'] });
    },
  });

  const blocked = tokenIsJwt;
  const valid =
    validateSiteId(form.site_id.trim()) === null &&
    form.label.trim() !== '' &&
    liveRetentionError === null &&
    historyRetentionError === null &&
    !blocked;

  const submit = () => {
    setTouched(true);
    if (!valid) return;
    commission.mutate();
  };

  return (
    <div style={{ maxWidth: 760 }}>
      <h2 style={{ marginTop: 0 }}>
        {existing ? `Update ${existing.label}` : 'Commission a site'}
      </h2>
      <p style={{ fontSize: 13, color: 'var(--nx-muted)' }}>
        Creates the database record and its deployment binding. This is idempotent — submitting for
        an existing site updates its binding and un-archives it, which is the supported way to
        bring a decommissioned site back.
      </p>
      <p style={{ fontSize: 13, color: 'var(--nx-warning)' }}>
        This does not put the site on the bus. Issuing the certificate, regenerating the ACL,
        restarting the platform bus and deploying the worker are host operations — this API holds
        neither <code>docker.sock</code> nor the CA key, by design. The checklist appears after
        submitting and stays until you dismiss it.
      </p>

      <TextField
        label="Site ID"
        required
        value={form.site_id}
        onChange={set('site_id')}
        error={siteIdError}
        placeholder="warehouse3"
        hint="Becomes the certificate CN and the bus ACL subject. Cannot be changed later without reissuing both."
      />
      <TextField label="Label" required value={form.label} onChange={set('label')} placeholder="Warehouse 3" />
      <TextField label="Timezone" value={form.timezone} onChange={set('timezone')} placeholder="UTC" />
      <TextField
        label="Task history retention — console (days)"
        type="number"
        value={form.task_live_retention_days}
        onChange={set('task_live_retention_days')}
        error={touched ? liveRetentionError : null}
        placeholder="30"
        hint="How long finished tasks stay in the operator console's Tasks tab. 7–365."
      />
      <TextField
        label="Task history retention — metrics/timeline (days)"
        type="number"
        value={form.task_history_retention_days}
        onChange={set('task_history_retention_days')}
        error={touched ? historyRetentionError : null}
        placeholder="90"
        hint="How long completion records and timeline logs stay in the database. 30–730, at least the console retention. Below 90 days truncates the Metrics screen's longest window."
      />

      <h3 style={{ fontSize: 12, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--nx-cyan)', marginTop: 24 }}>
        Bus binding
      </h3>
      <TextField
        label="Platform bus endpoint"
        value={form.platform_bus_endpoint}
        onChange={set('platform_bus_endpoint')}
        hint="Plane B. Without this the site cannot be granted bus access at all — gen_bus_acl.py skips it."
        placeholder="tls/zenoh-router-platform:7447"
      />
      <TextField
        label="Site router endpoint"
        value={form.zenoh_router_endpoint}
        onChange={set('zenoh_router_endpoint')}
        hint="Plane A, site-local ROS/RMW. Reachable only from inside the site; the worker is the only bridge between the planes."
        placeholder="tcp/zenoh-router-warehouse3:7447"
      />
      <TextField label="RMF session URI" value={form.rmf_session_uri} onChange={set('rmf_session_uri')} />
      <TextField
        label="Command key prefix"
        value={form.site_worker_command_keyexpr_prefix}
        onChange={set('site_worker_command_keyexpr_prefix')}
        hint="Defaults to nexsoss/v1/sites/<site_id> if left blank."
      />

      <h3 style={{ fontSize: 12, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--nx-cyan)', marginTop: 24 }}>
        Lifecycle broker
      </h3>
      <TextField label="Broker endpoint" value={form.broker_endpoint} onChange={set('broker_endpoint')} placeholder="http://rmf-lifecycle-broker-warehouse3:8088" />
      <TextField
        label="Broker token REFERENCE"
        value={form.broker_token_ref}
        onChange={set('broker_token_ref')}
        hint="An env var NAME or secret path — never the token itself. The database stores this string verbatim."
        placeholder="WAREHOUSE3_LIFECYCLE_BROKER_TOKEN"
        error={
          tokenIsJwt
            ? 'That is a JWT, not a reference. Storing a credential here would put it in the database in plaintext. Enter the name of the env var or secret that holds it.'
            : null
        }
      />
      {tokenLooksWrong && !tokenIsJwt && (
        <div
          role="alert"
          style={{
            border: '1px solid var(--nx-warning)',
            borderRadius: 'var(--nx-r)',
            padding: '8px 10px',
            fontSize: 13,
            color: 'var(--nx-warning)',
            marginBottom: 14,
          }}
        >
          This does not look like an env var name or a secret path. If it is the token itself, it
          will be written to the database in plaintext — put the token in the worker’s environment
          and reference it by name instead.
        </div>
      )}

      <h3 style={{ fontSize: 12, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--nx-cyan)', marginTop: 24 }}>
        Deployment
      </h3>
      <TextField label="Manifest version" value={form.deployment_manifest_version} onChange={set('deployment_manifest_version')} placeholder="0.0.0-dev" />
      <TextField label="Worker image digest" value={form.worker_image_digest} onChange={set('worker_image_digest')} placeholder="dev-local" />
      <TextField
        label="Certified capabilities"
        value={form.certified_capabilities}
        onChange={set('certified_capabilities')}
        hint="Comma-separated."
        placeholder="door.open, lift.request"
      />

      {labelError && (
        <div role="alert" style={{ color: 'var(--nx-error)', fontSize: 13 }}>
          Label is required.
        </div>
      )}
      {commission.isError && (
        <div role="alert" style={{ color: 'var(--nx-error)', fontSize: 13, marginBottom: 8 }}>
          {commission.error instanceof Error ? commission.error.message : 'Commission failed'}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <button type="button" onClick={submit} disabled={commission.isPending || blocked}>
          {commission.isPending ? 'Submitting…' : existing ? 'Update binding' : 'Commission site'}
        </button>
        <button type="button" onClick={() => { setForm(EMPTY); setResult(null); setTouched(false); }}>
          Reset
        </button>
      </div>

      {result && (
        <section
          data-testid="commission-result"
          style={{
            marginTop: 20,
            border: '1px solid var(--nx-border)',
            borderRadius: 'var(--nx-r)',
            padding: 16,
            background: 'var(--nx-bg2)',
          }}
        >
          <h3 style={{ marginTop: 0 }}>Applied</h3>
          <ul style={{ paddingLeft: 20 }}>
            {result.applied.map((line, i) => (
              <li key={i} style={{ fontSize: 13 }}>
                {line}
              </li>
            ))}
          </ul>
          <p style={{ fontSize: 13, color: 'var(--nx-muted)' }}>
            The remaining host steps are pinned at the top of every screen until you dismiss them.
          </p>
        </section>
      )}
    </div>
  );
}
